import React, { useEffect, useRef } from 'react';
import '../styles/FilterPanel.css';

interface FilterPanelProps {
  onFilterChange: (filters: string[]) => void;
  selectedFilters: string[];
  isMapView?: boolean;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  onFilterChange,
  selectedFilters,
  isMapView = false,
}) => {
  const swipeContainerRef = useRef<HTMLDivElement | null>(null);
  const selectedFiltersRef = useRef<string[]>(selectedFilters);

  // keep a ref of selectedFilters so native handlers can read latest value
  useEffect(() => {
    selectedFiltersRef.current = selectedFilters;
  }, [selectedFilters]);

  // Native fallback: attach native click/pointer handlers to filter buttons
  useEffect(() => {
    try {
      const busBtn = document.querySelector('button[aria-label="Toggle Swift ticket machine filter"]') as HTMLElement | null;
      const metroBtn = document.querySelector('button[aria-label="Toggle Metro ticket machine filter"]') as HTMLElement | null;
      const payzoneBtn = document.querySelector('button[aria-label="Toggle Payzone agents filter"]') as HTMLElement | null;

      const toggleFor = (filterType: string) => {
        const current = selectedFiltersRef.current || [];
        const updated = current.includes(filterType) ? current.filter(f => f !== filterType) : [...current, filterType];
        onFilterChange(updated);
      };

      const handlers: Array<() => void> = [];

      if (busBtn) {
        const h = () => toggleFor('BusStations');
        busBtn.addEventListener('click', h);
        handlers.push(() => busBtn.removeEventListener('click', h));
      }
      if (metroBtn) {
        const h = () => toggleFor('MetroStations');
        metroBtn.addEventListener('click', h);
        handlers.push(() => metroBtn.removeEventListener('click', h));
      }
      if (payzoneBtn) {
        const h = () => toggleFor('PayzoneAgents');
        payzoneBtn.addEventListener('click', h);
        handlers.push(() => payzoneBtn.removeEventListener('click', h));
      }

      return () => {
        handlers.forEach((fn) => {
          try { fn(); } catch (e) {}
        });
      };
    } catch (e) {
      // ignore
    }
    // only run on mount/unmount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (filterType: string) => {
    let updated: string[];
    if (selectedFilters.includes(filterType)) {
      updated = selectedFilters.filter((f) => f !== filterType);
    } else {
      updated = [...selectedFilters, filterType];
    }
    onFilterChange(updated);
  };

  const filterControls = (
    <div
      ref={swipeContainerRef}
      className={`filter-group wmnds-grid wmnds-m-b-sm`}
      role="group"
      aria-label="Filter by type"
    >
      <label>
        <input
          type="checkbox"
          className="wmnds-find-a-timetable-widget__mode-checkbox wmnds-screenreaders-only"
          name="mode"
          value="BusStations"
          checked={selectedFilters.includes('BusStations')}
          onChange={() => handleFilterChange('BusStations')}
        />
        <button
          type="button"
          className={`wmnds-btn wmnds-btn--mode wmnds-col-auto wmnds-m-r-sm wmnds-m-b-sm wmnds-p-xsm ${selectedFilters.includes('BusStations') ? 'wmnds-btn--selected' : ''}`}
          onClick={() => { handleFilterChange('BusStations') }}
          aria-pressed={selectedFilters.includes('BusStations')}
          aria-label="Toggle Swift ticket machine filter"
        >
          <svg className="wmnds-btn__icon">
            <use xlinkHref="#wmnds-swift-bird-icon" href="#wmnds-swift-bird-icon"></use>
          </svg>
          Swift ticket machine
        </button>
      </label>

      <label>
        <input
          type="checkbox"
          className="wmnds-find-a-timetable-widget__mode-checkbox wmnds-screenreaders-only"
          name="mode"
          value="MetroStations"
          checked={selectedFilters.includes('MetroStations')}
          onChange={() => handleFilterChange('MetroStations')}
        />
        <button
          type="button"
          className={`wmnds-btn wmnds-btn--mode wmnds-col-auto wmnds-m-r-sm wmnds-m-b-sm wmnds-p-xsm ${selectedFilters.includes('MetroStations') ? 'wmnds-btn--selected' : ''}`}
          onClick={() => { handleFilterChange('MetroStations') }}
          aria-pressed={selectedFilters.includes('MetroStations')}
          aria-label="Toggle Metro ticket machine filter"
        >
          <svg className="wmnds-btn__icon">
            <use xlinkHref="#wmnds-modes-isolated-metro" href="#wmnds-modes-isolated-metro"></use>
          </svg>
          Metro ticket machine
        </button>
      </label>

      <label>
        <input
          type="checkbox"
          className="wmnds-find-a-timetable-widget__mode-checkbox wmnds-screenreaders-only"
          name="mode"
          value="PayzoneAgents"
          checked={selectedFilters.includes('PayzoneAgents')}
          onChange={() => handleFilterChange('PayzoneAgents')}
        />
        <button
          type="button"
          className={`wmnds-btn wmnds-btn--mode wmnds-col-auto wmnds-m-r-sm wmnds-m-b-sm wmnds-p-xsm ${selectedFilters.includes('PayzoneAgents') ? 'wmnds-btn--selected' : ''}`}
          onClick={() => { handleFilterChange('PayzoneAgents') }}
          aria-pressed={selectedFilters.includes('PayzoneAgents')}
          aria-label="Toggle Payzone agents filter"
        >
          <svg className="wmnds-btn__icon">
            <use xlinkHref="#wmnds-general-id-card" href="#wmnds-general-id-card"></use>
          </svg>
          Payzone
        </button>
      </label>
    </div>
  );

  return (
    <div className="filter-panel wmnds-fixed">
      <div className="wmnds-col-1"><h2 className="wmnds-h4">Filter by Type</h2></div>

      {filterControls}

      {/* <div className="filter-buttons">
        <button
          onClick={handleSelectAll}
          className="wmnds-btn wmnds-btn--primary"
        >
          Select All
        </button>
        <button
          onClick={handleClearAll}
          className="wmnds-btn wmnds-btn--secondary"
        >
          Clear All
        </button>
      </div> */}
    </div>
  );
};

export default FilterPanel;
